// src/pages/Contact/Contact.jsx
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Paper,
  TextField,
  Button,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Phone,
  Email,
  LocationOn,
  Person,
  Message,
  Send,
  AccessTime,
  LinkedIn,
  Twitter,
  Facebook,
  Instagram,
  ArrowForward,
  CheckCircle,
  Business,
  Public,
  WhatsApp,
  YouTube,
} from "@mui/icons-material";
import emailjs from "@emailjs/browser";
import styles from "./Contact.module.css";

// Initialize EmailJS
emailjs.init("YOUR_EMAILJS_PUBLIC_KEY");

const Contact = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Contact Info
  const contactInfo = [
    {
      icon: <LocationOn sx={{ fontSize: 28 }} />,
      title: "Visit Us",
      details: ["123 Tech Hub, London", "United Kingdom"],
      color: "#8cc61d",
      bg: "#8cc61d10",
    },
    {
      icon: <Phone sx={{ fontSize: 28 }} />,
      title: "Call Us",
      details: ["+44 20 7123 4567", "+44 20 7123 4568"],
      color: "#4a90d9",
      bg: "#4a90d910",
    },
    {
      icon: <Email sx={{ fontSize: 28 }} />,
      title: "Email Us",
      details: ["info@wavelength-uk.com", "support@wavelength-uk.com"],
      color: "#9b59b6",
      bg: "#9b59b610",
    },
    {
      icon: <AccessTime sx={{ fontSize: 28 }} />,
      title: "Working Hours",
      details: ["Mon - Fri: 9:00 - 18:00", "Sat: 10:00 - 14:00"],
      color: "#f39c12",
      bg: "#f39c1210",
    },
  ];

  // Social Media Links
  const socialLinks = [
    { icon: <LinkedIn />, label: "LinkedIn", color: "#0077b5" },
    { icon: <Twitter />, label: "Twitter", color: "#1da1f2" },
    { icon: <Facebook />, label: "Facebook", color: "#1877f2" },
    { icon: <Instagram />, label: "Instagram", color: "#e4405f" },
    { icon: <WhatsApp />, label: "WhatsApp", color: "#25D366" },
    { icon: <YouTube />, label: "YouTube", color: "#FF0000" },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      subject: formData.subject,
      message: formData.message,
      to_email: "waveserv2@gmail.com",
      reply_to: formData.email,
    };

    try {
      await emailjs.send("YOUR_SERVICE_ID", "YOUR_TEMPLATE_ID", templateParams);

      setSnackbar({
        open: true,
        message: "Thank you! We will get back to you within 24 hours.",
        severity: "success",
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again or call us directly.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box className={styles.contactPage}>
      {/* Hero Section with Background */}
      <Box className={styles.heroSection}>
        <Container maxWidth="lg">
          <Box className={styles.heroContent}>
            <Chip
              label="📞 Get In Touch"
              className={styles.heroChip}
              size="small"
            />
            <Typography
              variant="h1"
              className={styles.heroTitle}
              sx={{
                fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4rem" },
              }}
            >
              Let's <span className={styles.highlight}>Connect</span>
            </Typography>
            <Typography variant="h6" className={styles.heroSubtitle}>
              We'd love to hear from you. Reach out and let's start a
              conversation about your next project.
            </Typography>
            <Box className={styles.heroStats}>
              <Box className={styles.heroStat}>
                <Typography variant="h4" className={styles.heroStatNumber}>
                  24/7
                </Typography>
                <Typography variant="caption">Support Available</Typography>
              </Box>
              <Box className={styles.heroStat}>
                <Typography variant="h4" className={styles.heroStatNumber}>
                  100%
                </Typography>
                <Typography variant="caption">Client Satisfaction</Typography>
              </Box>
              <Box className={styles.heroStat}>
                <Typography variant="h4" className={styles.heroStatNumber}>
                  1hr
                </Typography>
                <Typography variant="caption">Response Time</Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Contact Info Cards */}
      <Box className={styles.infoSection}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {contactInfo.map((info, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Paper elevation={0} className={styles.infoCard}>
                  <Box
                    className={styles.infoIcon}
                    sx={{ background: info.bg, color: info.color }}
                  >
                    {info.icon}
                  </Box>
                  <Typography variant="h6" className={styles.infoTitle}>
                    {info.title}
                  </Typography>
                  {info.details.map((detail, idx) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      className={styles.infoDetail}
                    >
                      {detail}
                    </Typography>
                  ))}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Form & Map Section */}
      <Box className={styles.formSection}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {/* Left Side - Form */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Box className={styles.formContent}>
                <Chip
                  label="✉️ Send Us a Message"
                  className={styles.formChip}
                  size="small"
                />
                <Typography
                  variant="h2"
                  className={styles.sectionTitle}
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
                  }}
                >
                  Get In <span className={styles.highlight}>Touch</span>
                </Typography>
                <Typography variant="body1" className={styles.sectionSubtitle}>
                  Fill in the form below and our team will get back to you
                  within 24 hours.
                </Typography>

                <form onSubmit={handleSubmit} className={styles.contactForm}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className={styles.formField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person sx={{ color: "#8cc61d" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={styles.formField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email sx={{ color: "#8cc61d" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className={styles.formField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone sx={{ color: "#8cc61d" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className={styles.formField}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Message sx={{ color: "#8cc61d" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Your Message"
                        name="message"
                        multiline
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        required
                        className={styles.formField}
                        placeholder="Tell us about your project, partnership, or any questions you have..."
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Message sx={{ color: "#8cc61d" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        className={styles.submitBtn}
                        disabled={loading}
                        endIcon={!loading && <Send />}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          "Send Message"
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </form>

                <Typography variant="caption" className={styles.secureText}>
                  🔒 Your information is secure and will not be shared with
                  third parties.
                </Typography>
              </Box>
            </Grid>

            {/* Right Side - Map & Info */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Box className={styles.mapContent}>
                <Paper elevation={0} className={styles.mapCard}>
                  <Typography variant="h6" className={styles.mapTitle}>
                    📍 Find Us
                  </Typography>
                  <Box className={styles.mapContainer}>
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2483.678690269771!2d-0.127647484229145!3d51.50721787963524!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487604c7f4f9e5c7%3A0x6b3d9f9b6c4b6c4a!2sLondon%2C%20UK!5e0!3m2!1sen!2s!4v1700000000000"
                      width="100%"
                      height="280"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Wavelength Office Location"
                      className={styles.mapIframe}
                    />
                  </Box>

                  <Divider className={styles.mapDivider} />

                  <Box className={styles.mapDetails}>
                    <Box className={styles.mapDetailItem}>
                      <Business className={styles.mapDetailIcon} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Wavelength Consultancy Solutions
                        </Typography>
                        <Typography variant="caption" color="#3f5a79">
                          123 Tech Hub, London, United Kingdom
                        </Typography>
                      </Box>
                    </Box>
                    <Box className={styles.mapDetailItem}>
                      <Public className={styles.mapDetailIcon} />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          Global Presence
                        </Typography>
                        <Typography variant="caption" color="#3f5a79">
                          UK · Nigeria · International
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Divider className={styles.mapDivider} />

                  {/* Social Links */}
                  <Box className={styles.socialSection}>
                    <Typography
                      variant="subtitle2"
                      className={styles.socialTitle}
                    >
                      Connect With Us
                    </Typography>
                    <Box className={styles.socialIcons}>
                      {socialLinks.map((social, index) => (
                        <IconButton
                          key={index}
                          className={styles.socialIcon}
                          sx={{
                            "&:hover": {
                              color: social.color,
                              background: `${social.color}10`,
                              borderColor: social.color,
                              transform: "translateY(-3px)",
                            },
                          }}
                          aria-label={social.label}
                        >
                          {social.icon}
                        </IconButton>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box className={styles.ctaSection}>
        <Container maxWidth="lg">
          <Box className={styles.ctaContent}>
            <Typography variant="h3" className={styles.ctaTitle}>
              Ready to <span className={styles.highlight}>Transform</span> Your
              Business?
            </Typography>
            <Typography variant="body1" className={styles.ctaText}>
              Let's discuss how we can help you with innovative technology
              solutions.
            </Typography>
            <Box className={styles.ctaButtons}>
              <Button
                variant="contained"
                className={styles.ctaPrimaryBtn}
                startIcon={<Phone />}
              >
                Call Us Now
              </Button>
              <Button
                variant="outlined"
                className={styles.ctaSecondaryBtn}
                startIcon={<Email />}
              >
                Email Us
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;
